import axios from 'axios';

const api = axios.create({ baseURL: '/api' });

// Attach JWT token to every request
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('auth_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// On 401 — clear token and redirect to login
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('auth_token');
      localStorage.removeItem('auth_user');
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

export default api;

// ─── Auth ─────────────────────────────────────────────────────────────────────

export const login = (username, password) =>
  axios.post('/auth/login', { username, password }).then((r) => r.data);

export const getMe = () =>
  api.get('/auth/me').then((r) => r.data);

// ─── Dashboard ────────────────────────────────────────────────────────────────

export const getDashboard = () => api.get('/dashboard').then(r => r.data);
export const getBankBalances = () => api.get('/banks/balance').then(r => r.data);
export const getBankOverdraftCost = (bank = 'tinkoff') =>
  api.get('/banks/overdraft-cost', { params: { bank } }).then(r => r.data);
export const getBankTransactions = (bank, days = 30, search = '') =>
  api.get('/banks/transactions', { params: { bank, days, search: search || undefined } }).then(r => r.data);
export const getBankSummary = (days = 30, bank = '') =>
  api.get('/banks/summary', { params: { days, bank: bank || undefined } }).then(r => r.data);
export const getBankSummaryByDate = (date, bank = '') =>
  api.get('/banks/summary', { params: { date, bank: bank || undefined } }).then(r => r.data);
export const getBankSummaryByMonth = (month, bank = '') =>
  api.get('/banks/summary', { params: { month, bank: bank || undefined } }).then(r => r.data);

export const getSalesSummary = () => api.get('/sales/summary').then(r => r.data);
export const getSalesOrders = (days = 30) =>
  api.get('/sales/orders', { params: { days } }).then(r => r.data);
export const getSalesTopCustomers = (days = 30) =>
  api.get('/sales/top_customers', { params: { days } }).then(r => r.data);

export const getStock = (lowStock = false) =>
  api.get('/stock', { params: { low_stock: lowStock } }).then(r => r.data);

export const getCrmFunnel = () => api.get('/crm/funnel').then(r => r.data);
export const getCrmDeals = () => api.get('/crm/deals').then(r => r.data);
export const getCrmLeads = () => api.get('/crm/leads').then(r => r.data);
export const getCrmLeadsFunnel = () => api.get('/crm/leads/funnel').then(r => r.data);
export const getCrmPipelines = () => api.get('/crm/pipelines').then(r => r.data);
export const getCrmTasks = () => api.get('/crm/tasks').then(r => r.data);
export const getCrmChats = () => api.get('/crm/chats').then(r => r.data);

export const getOnecPing = () =>
  api.get('/onec/ping').then(r => r.data);
export const getOnecDebts = (type) =>
  api.get('/onec/debts', { params: { debt_type: type } }).then(r => r.data);
export const getOnecPayroll = (period) =>
  api.get('/onec/payroll', { params: { period } }).then(r => r.data);
export const uploadPayroll = (file) => {
  const form = new FormData();
  form.append('file', file);
  return api.post('/payroll/upload', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }).then(r => r.data);
};
export const getPayrollPeriods = () =>
  api.get('/payroll/periods').then(r => r.data);

// ─── Рабочий стол бухгалтера ──────────────────────────────────────────────────
export const getPayrollEmployees = () =>
  api.get('/payroll/employees').then(r => r.data);
export const savePayrollEmployee = (id, body) =>
  id ? api.put(`/payroll/employees/${id}`, body).then(r => r.data)
     : api.post('/payroll/employees', body).then(r => r.data);
export const deletePayrollEmployee = (id) =>
  api.delete(`/payroll/employees/${id}`).then(r => r.data);
export const dismissPayrollEmployee = (id) =>
  api.post(`/payroll/employees/${id}/dismiss`).then(r => r.data);
export const restorePayrollEmployee = (id) =>
  api.post(`/payroll/employees/${id}/restore`).then(r => r.data);
export const importEmployeesFromExcel = () =>
  api.post('/payroll/employees/import-from-excel').then(r => r.data);

export const getPayrollRecords = (period) =>
  api.get('/payroll/records', { params: { period } }).then(r => r.data);
export const savePayrollRecordsBatch = (body) =>
  api.post('/payroll/records/batch', body).then(r => r.data);
export const initPayrollPeriod = (period) =>
  api.post('/payroll/records/init-period', null, { params: { period } }).then(r => r.data);
export const getPayrollSheet = (period) =>
  api.get('/payroll/sheet', { params: { period } }).then(r => r.data);
export const savePayrollSheet = (body) =>
  api.post('/payroll/sheet', body).then(r => r.data);
export const seedPayrollRoster = () =>
  api.post('/payroll/seed-roster').then(r => r.data);
export const getOnecStock = () =>
  api.get('/onec/stock', { timeout: 300000 }).then(r => r.data);
export const getOnecMargin = (period) =>
  api.get('/onec/margin', { params: { period }, timeout: 300000 }).then(r => r.data);
export const getOnecStockValue = () =>
  api.get('/onec/stock-value', { timeout: 300000 }).then(r => r.data);
export const getOnecToday = () =>
  api.get('/onec/today', { timeout: 60000 }).then(r => r.data);
export const refreshOnecCache = () =>
  api.post('/onec/refresh').then(r => r.data);

// Priority dashboard endpoints
export const getDashboardPriority = () =>
  api.get('/dashboard/priority', { timeout: 15000 }).then(r => r.data);
export const getDashboardStock = () =>
  api.get('/dashboard/stock', { timeout: 300000 }).then(r => r.data);

export const getFinDirectorSalesReport = (period = 'week', date_from = '', date_to = '', exclude_own_retail = false) => {
  const params = { period };
  if (date_from) params.date_from = date_from;
  if (date_to) params.date_to = date_to;
  if (exclude_own_retail) params.exclude_own_retail = 'true';
  return api.get('/findirector/sales-report', { params, timeout: 120000 }).then(r => r.data);
};

export const getAcademySalesReport = getFinDirectorSalesReport;

export const getFinDirectorWriteoffReport = (period = 'month', date_from = '', date_to = '') => {
  const params = { period };
  if (date_from) params.date_from = date_from;
  if (date_to) params.date_to = date_to;
  return api.get('/findirector/writeoff-report', { params, timeout: 120000 }).then(r => r.data);
};

export const exportFinDirectorWriteoffReportXlsx = (period = 'month', date_from = '', date_to = '') => {
  const params = { period };
  if (date_from) params.date_from = date_from;
  if (date_to) params.date_to = date_to;
  return api.get('/findirector/writeoff-report/export/xlsx', {
    params,
    timeout: 120000,
    responseType: 'blob',
  }).then(r => r.data);
};

export const getFinDirectorFinanceSummary = (period) =>
  api.get('/findirector/finance-summary', { params: { period }, timeout: 60000 }).then(r => r.data);

export const getAcademyFinanceSummary = getFinDirectorFinanceSummary;

export const getDeadStockList = () =>
  api.get('/findirector/dead-stock', { timeout: 60000 }).then(r => r.data);
export const assignDeadStock = (body) =>
  api.post('/findirector/dead-stock/assign', body).then(r => r.data);
export const markDeadStockSold = (taskId, body) =>
  api.post(`/findirector/dead-stock/${taskId}/sold`, body).then(r => r.data);
export const getDeadStockProgress = () =>
  api.get('/findirector/dead-stock/progress').then(r => r.data);
export const cancelDeadStockTask = (taskId) =>
  api.delete(`/findirector/dead-stock/${taskId}`).then(r => r.data);

export const getProcurementReceipts = () =>
  api.get('/procurement/receipts').then(r => r.data);

export const getProcurementReceiptDetail = (receiptId) =>
  api.get(`/procurement/receipts/${receiptId}`, { timeout: 120000 }).then(r => r.data);

export const exportProcurementReceiptXlsx = (receiptId) =>
  api.get(`/procurement/receipts/${receiptId}/export/xlsx`, {
    timeout: 120000,
    responseType: 'blob',
  }).then(r => r.data);

export const getPlanFact = (period) =>
  api.get('/planfact', { params: { period } }).then(r => r.data);
export const setPlan = (period, category, planned_value) =>
  api.post('/planfact', { period, category, planned_value }).then(r => r.data);

export const getRetailCash = () => api.get('/banks/retail-cash').then(r => r.data);
export const getFinDirectorCash = () => api.get('/banks/findirector-cash').then(r => r.data);
export const postFinDirectorCash = (amount, direction, description = '', branch = '') =>
  api.post('/banks/findirector-cash', { amount, direction, description, branch }).then(r => r.data);

export const getAcademyCash = getFinDirectorCash;
export const postAcademyCash = postFinDirectorCash;

export const getRetailSummary = () => api.get('/retail/summary').then(r => r.data);
export const getRetailBranches = () => api.get('/retail/branches').then(r => r.data);

export const getSheets = () => api.get('/sheets').then(r => r.data);
export const triggerSync = () => api.post('/sync').then(r => r.data);
export const getSyncLogs = () => api.get('/sync/logs').then(r => r.data);

// ─── AI РОП Академии ─────────────────────────────────────────────────────────
export const getAiRopOverview = () =>
  api.get('/ai-rop/overview', { timeout: 180000 }).then(r => r.data);
export const runAiRopDaily = (dryRun = true) =>
  api.post('/ai-rop/run-daily', { dry_run: dryRun }, { timeout: 240000 }).then(r => r.data);
export const sendAiRopChat = (message) =>
  api.post('/ai-rop/chat', { message }, { timeout: 180000 }).then(r => r.data);
export const approveAiRopAction = (id) =>
  api.post(`/ai-rop/actions/${id}/approve`).then(r => r.data);
export const executeAiRopAction = (id) =>
  api.post(`/ai-rop/actions/${id}/execute`, null, { timeout: 120000 }).then(r => r.data);

// ─── SEO дашборд (Яндекс.Метрика) ─────────────────────────────────────────────
export const getSeoDashboard = (refresh = false) =>
  api.get('/seo/dashboard', { params: refresh ? { refresh: 1 } : {}, timeout: 120000 }).then(r => r.data);

export const getSeoCounters = (refresh = false) =>
  api.get('/seo/counters', { params: refresh ? { refresh: 1 } : {}, timeout: 60000 }).then(r => r.data);

export const getMarketingFunnel = (refresh = false) =>
  api.get('/marketing/retail-funnel', {
    params: refresh ? { refresh: 1 } : {},
    timeout: 180000,
  }).then(r => r.data);

export const getSeoSite = (counter, date1, date2, refresh = false) =>
  api.get('/seo/site', {
    params: { counter, date1, date2, ...(refresh ? { refresh: 1 } : {}) },
    timeout: 120000,
  }).then(r => r.data);

// PlanFact.io
export const getPfAccounts = () => api.get('/planfact/accounts').then(r => r.data);
export const getPfPnl = (months = 6) => api.get('/planfact/pnl', { params: { months } }).then(r => r.data);
export const getPfOperations = (params = {}) => api.get('/planfact/operations', { params }).then(r => r.data);
export const getPfCompanies = () => api.get('/planfact/companies').then(r => r.data);
